(() => {
  if (window.ncSeedContentCleanup) {
    window.ncSeedContentCleanup.mount(document);
    return;
  }
  const text = value => String(value || '').replace(/\s+/g, ' ').trim();
  const observed = new WeakSet();
  const contentSelector = '.product__description, .nc-seed-static-specs, .nc-seed-catalog-intro, .nc-seed-diversity-guidance, [data-selected-details]';
  const inventorySummary = /^(?:Core:\s*\d+ species for the foundation|(?:Expanded|Maximum):\s*what \d+ additional species contribute|(?:Expanded|Maximum) Diversity Add-On:\s*every species and its contribution)\s*\+?$/i;
  const legalHosts = new Set(['creativecommons.org', 'opendatacommons.org']);
  const platformHosts = new Set(['cdn.shopify.com']);
  const normalizeHost = host => host.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
  const isExternalReference = (anchor, scope) => {
    let url;
    try { url = new URL(anchor.getAttribute('href'), document.baseURI); } catch (_) { return false; }
    if (!/^https?:$/.test(url.protocol)) return false;
    const host = normalizeHost(url.hostname);
    const storeHosts = new Set([location.hostname, 'naturalcommunities.net', ...(scope.dataset.seedStoreHosts || '').split(',')].map(normalizeHost));
    if (storeHosts.has(host) || legalHosts.has(host) || platformHosts.has(host)) return false;
    // Preserve explicit license notices and legal documents; only informational copy is in scope.
    if (anchor.rel.split(/\s+/).some(value => /^(?:license|cc:attributionURL|dct:creator)$/i.test(value))) return false;
    const notice = anchor.closest('p, figcaption');
    if (notice?.querySelector('a[rel~="license"]') && /^(?:(?:photo|image)\s*(?:©|copyright)|©|copyright)/i.test(text(notice.textContent))) return false;
    return true;
  };
  const canonicalRecipeAvailable = scope => {
    if (scope.querySelector('#mix-specifications table, .nc-seed-static-specs table')) return true;
    const builder = scope.querySelector('nc-prairie-builder');
    if (!builder?.querySelector('[data-selected-details]')) return false;
    try {
      const rows = JSON.parse(builder.querySelector('[data-species]')?.textContent || '[]');
      return Array.isArray(rows) && rows.length > 0;
    } catch (_) { return false; }
  };
  const cleanup = scope => {
    if (!scope?.matches('product-info.nc-seed-catalog')) return;
    const copies = Array.from(scope.querySelectorAll('.product__description'));
    if (canonicalRecipeAvailable(scope) && scope.querySelector('nc-prairie-builder')) {
      copies.forEach(copy => {
        const parents = new Set();
        copy.querySelectorAll('details').forEach(details => {
          const summary = Array.from(details.children).find(child => child.tagName === 'SUMMARY');
          if (!summary || !inventorySummary.test(text(summary.textContent))) return;
          if (!details.querySelector('ul.nc-associates') || details.closest('.nc-prairie-highlights')) return;
          // Remove only the audited exhaustive contribution lists. Highlight sections and
          // all numeric recipe/coverage tables are deliberately outside this selector.
          parents.add(details.parentElement);
          details.remove();
        });
        // Wait for the builder's grouping: removing this routing heading earlier
        // would incorrectly place the highlight sections in the coverage group.
        copy.querySelectorAll('.nc-prairie__content-group h2').forEach(heading => {
          if (text(heading.textContent) !== 'Blend ingredients') return;
          const group = heading.closest('.nc-prairie__content-group');
          const details = Array.from(group.children).find(node => node.tagName === 'DETAILS');
          const summary = heading.nextElementSibling;
          if (!details || !/-2$/.test(details.id) || summary?.tagName !== 'P' || summary.querySelector('a')) return;
          const exactComponentTotal = /^\d+ species; \d+(?:\.\d+)? PLS lb\/acre; \d+(?:\.\d+)? seeds\/sq ft\. Quoted proportions are by PLS weight, not mature cover\.$/;
          if (!exactComponentTotal.test(text(summary.textContent))) return;
          heading.remove();
          summary.remove();
        });
        parents.forEach(parent => {
          Array.from(parent.children).forEach(node => {
            if (node.tagName === 'H2' && text(node.textContent) === 'Why these species belong in the planting') node.remove();
            if (node.tagName === 'P' && text(node.textContent) === 'Each species contributes a different combination of growth form, season or habitat value. The notes below describe established plants on suitable sites; they do not guarantee germination, abundance or wildlife visits.') {
              node.textContent = 'These highlights describe established plants on suitable sites; they do not guarantee germination, abundance or wildlife visits.';
            }
          });
        });
      });
    }
    const roots = Array.from(scope.querySelectorAll(contentSelector));
    const anchors = new Set(roots.flatMap(root => Array.from(root.querySelectorAll('a[href]'))));
    anchors.forEach(anchor => {
      if (!isExternalReference(anchor, scope)) return;
      if (/^species reference$/i.test(text(anchor.textContent))) anchor.remove();
      else {
        // Keep named citations and useful text, including any inline formatting.
        const replacement = document.createElement('span');
        for (const name of ['id', 'class', 'title', 'lang', 'dir']) {
          if (anchor.hasAttribute(name)) replacement.setAttribute(name, anchor.getAttribute(name));
        }
        replacement.append(...Array.from(anchor.childNodes));
        anchor.replaceWith(replacement);
      }
    });
  };
  const mount = root => {
    const scopes = [...(root.matches?.('product-info.nc-seed-catalog') ? [root] : []), ...root.querySelectorAll('product-info.nc-seed-catalog')];
    scopes.forEach(scope => {
      cleanup(scope);
      if (observed.has(scope)) return;
      observed.add(scope);
      let queued = false;
      const observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => { queued = false; if (scope.isConnected) cleanup(scope); });
      });
      // The builder and legacy layout may organize content after this asset runs.
      // Watching insertions also cleans recipe rows added after a selection change.
      observer.observe(scope, {childList: true, subtree: true});
    });
  };
  window.ncSeedContentCleanup = {cleanup, mount};
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
})();
